import React, { useState, useEffect, createContext, useRef } from 'react';
import axios from 'axios';
import './App.css';
import Login from './components/Login';
import Home from './pages/Home';
import FieldSignal from './pages/FieldSignal';
import FieldIntel from './pages/FieldIntel';
import SentimentIntel from './pages/SentimentIntel';
import FieldOps from './pages/FieldOps';
import FieldStrategy from './pages/FieldStrategy';
import FieldHQ from './pages/FieldHQ';
import DachidoAdmin from './pages/DachidoAdmin';
import AdminTasks from './pages/AdminTasks';
import DrilldownPanel from './components/DrilldownPanel';
import AgriHome from './pages/AgriHome';
import AgriMarketing from './pages/AgriMarketing';
import AgriOperations from './pages/AgriOperations';
import AgriEngagement from './pages/AgriEngagement';
import AgriAudio from './pages/AgriAudio';
import HrRetention from './pages/HrRetention';
import HrExplorer from './pages/HrExplorer';
import CustomWidgets from './pages/CustomWidgets';
import System from './pages/System';
import OrgConfig from './pages/OrgConfig';
import AgentView from './pages/AgentView';
import AgentCompare from './pages/AgentCompare';
import PageWidgets from './components/PageWidgets';
import MarketBrief from './pages/MarketBrief';
import BrandIntelV2 from './pages/BrandIntelV2';
import FieldIntelV2 from './pages/FieldIntelV2';
import Analytics from './pages/Analytics';
import FieldTeamV2 from './pages/FieldTeamV2';
import Help from './pages/Help';
import { ReportProvider, ReportsMenu } from './components/ReportWorkspace';

import { setEntityTypeLabels } from './utils/entityLabels';

export const FilterContext = createContext();

// The Flask session cookie is per-browser, not per-tab. If a second tab
// logs in as a different org, the cookie is overwritten and this tab starts
// getting the other org's data. The backend returns 409 ORG_MISMATCH when
// our X-Organization header disagrees with the cookie; here we hard-reset
// so the user re-authenticates instead of silently seeing wrong-org rows.
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 409 && err.response.data?.code === 'ORG_MISMATCH') {
      try {
        localStorage.removeItem('fieldforce_auth');
      } catch (_) {}
      delete axios.defaults.headers.common['X-Organization'];
      delete axios.defaults.headers.common['X-Db-Choice'];
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [currentModule, setCurrentModule] = useState('sales-home');
  const [filters, setFilters] = useState({
    time_range: '365',
    date_from: null,
    date_to: null,
    // Geography is two levels and both are multi-select: province is dim_region.state,
    // region_id is the city (dim_region.region_name). Held as arrays, or null when
    // nothing is picked — an empty array would still serialize as "region_id=" because
    // every page's param loop guards on `v !== ''`, which an array never equals.
    // Province lives here rather than in FilterBar because FilterBar remounts on every
    // tab switch; a province that silently reset would widen the city list back to
    // everything while the data stayed filtered.
    province: null,
    region_id: null,
    team_id: null,
    channel_id: null,
    client_type_id: null,
    agent_id: null,
    date_by: 'call_date', // 'call_date' = conversation date; 'processed' = when recorded was processed (tiles only)
  });
  const [drilldown, setDrilldown] = useState(null);
  const [loading, setLoading] = useState(false);
  const [focusedConversationId, setFocusedConversationId] = useState(null);

  const storedAuth = (() => {
    try {
      return JSON.parse(localStorage.getItem('fieldforce_auth') || '{}');
    } catch (_) {
      return {};
    }
  })();
  const [isStaging, setIsStaging] = useState(!!(storedAuth.requested_db === 'staging'));
  const [isStagingMachine, setIsStagingMachine] = useState(!!(storedAuth.is_staging));

  // Per-org tab assignment fetched from /api/config/org-tabs
  const [orgTabs, setOrgTabs] = useState(null);
  // Full module registry fetched from /api/config/modules
  const [moduleRegistry, setModuleRegistry] = useState([]);
  // Per-org header branding fetched from /api/config/org-branding
  const [branding, setBranding] = useState({ title: null, logo_url: null });

  // Apply staging_only flag from registry client-side
  const allModules = moduleRegistry.map(m =>
    m.staging_only ? { ...m, disabled: !isStagingMachine } : m
  );

  const moduleMap = Object.fromEntries(allModules.map(m => [m.id, m]));
  const modules = orgTabs
    ? orgTabs.map(id => moduleMap[id]).filter(m => m && !m.disabled)
    : allModules.filter(m => !m.disabled);

  // Auto-logout after 30 minutes of inactivity
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  useEffect(() => {
    if (!isAuthenticated) return;

    const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes

    const resetTimer = () => {
      lastActivityRef.current = Date.now();
      
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      inactivityTimerRef.current = setTimeout(() => {
        const timeSinceLastActivity = Date.now() - lastActivityRef.current;
        if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
          alert('You have been logged out due to inactivity (30 minutes)');
          handleLogout();
        }
      }, INACTIVITY_TIMEOUT);
    };

    // Track user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, resetTimer, true);
    });

    resetTimer(); // Initialize timer

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer, true);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [isAuthenticated]);

  // Check authentication on mount; server is source of truth for db_choice (verify returns it on staging).
  useEffect(() => {
    const authData = localStorage.getItem('fieldforce_auth');
    if (authData) {
      try {
        const parsedAuth = JSON.parse(authData);
        const requestedDbFromStorage = parsedAuth.requested_db;
        setIsStaging(requestedDbFromStorage === 'staging');

        axios.post('/api/auth/verify', {
          organization: parsedAuth.organization,
          username: parsedAuth.username
        })
        .then(async (response) => {
          if (response.data.valid) {
            // Re-detect staging machine by checking /api/db/choice
            let machineIsStaging = !!(parsedAuth.is_staging);
            try {
              const dbRes = await axios.get('/api/db/choice');
              if (dbRes.data && typeof dbRes.data.db_choice === 'string') {
                machineIsStaging = true;
              }
            } catch (_) {}
            setIsStagingMachine(machineIsStaging);

            const serverDbChoice = response.data.db_choice;
            const requestedDb = serverDbChoice != null ? serverDbChoice : requestedDbFromStorage;
            setUser({ ...response.data.user, requested_db: requestedDb });
            setIsStaging(requestedDb === 'staging');
            axios.defaults.headers.common['X-Organization'] = response.data.user.organization;
            if (requestedDb && (requestedDb === 'staging' || requestedDb === 'production')) {
              try {
                await axios.post('/api/db/choice', { db_choice: requestedDb });
              } catch (_) {}
              axios.defaults.headers.common['X-Db-Choice'] = requestedDb;
            } else {
              delete axios.defaults.headers.common['X-Db-Choice'];
            }

            // Update localStorage with current staging state
            const updatedAuth = { ...parsedAuth, is_staging: machineIsStaging, requested_db: requestedDb };
            localStorage.setItem('fieldforce_auth', JSON.stringify(updatedAuth));

            setIsAuthenticated(true);
            setLoading(true);
          } else {
            localStorage.removeItem('fieldforce_auth');
            delete axios.defaults.headers.common['X-Organization'];
            setIsAuthenticated(false);
          }
        })
        .catch(err => {
          console.error('Session verification failed:', err);
          localStorage.removeItem('fieldforce_auth');
          delete axios.defaults.headers.common['X-Organization'];
          setIsAuthenticated(false);
        });
      } catch (err) {
        console.error('Invalid auth data:', err);
        localStorage.removeItem('fieldforce_auth');
        setIsAuthenticated(false);
      }
    }
  }, []);

  // Load filter dimensions, module registry, and org tab config when authenticated.
  // Module registry and org-tabs are fetched together via Promise.all so that both
  // states are set in the same render — preventing a flash of all modules while one
  // has loaded but the other hasn't yet.
  useEffect(() => {
    if (isAuthenticated) {
      axios.get('/api/config/org-settings')
        .then(res => {
          const s = res.data?.settings || {};
          if (s.default_time_range) setFilters(f => ({ ...f, time_range: s.default_time_range }));
        })
        .catch(() => {});

      const orgParam = user?.organization || storedAuth.organization || '';
      Promise.all([
        axios.get('/api/config/modules'),
        axios.get('/api/config/org-tabs', { params: { organization: orgParam } }),
      ])
        .then(([modsRes, tabsRes]) => {
          const mods = modsRes.data?.modules;
          if (mods && mods.length > 0) setModuleRegistry(mods);

          // Installed before any page renders a type name, so "Product" is labelled
          // "Brand" from the first paint rather than flipping after it.
          setEntityTypeLabels(tabsRes.data?.entity_type_labels);

          const tabs = tabsRes.data?.tabs;
          if (tabs && tabs.length > 0) {
            setOrgTabs(tabs);
            // Default to first org tab if current module is not in the enabled set
            const enabledSet = new Set(tabs);
            if (!enabledSet.has(currentModule)) {
              setLoading(true);
              setCurrentModule(tabs[0]);
            }
          }
        })
        .catch(err => console.error('Error loading module config:', err));
    }
  }, [isAuthenticated, currentModule, storedAuth.organization, user?.organization]);

  // Org branding (title + logo) — fetched once per org and cached in localStorage.
  // The logo SAS URL is valid for 24h, so we cap the cache at 12h to stay well under.
  useEffect(() => {
    if (!isAuthenticated) return;
    const orgKey = (user?.organization || storedAuth.organization || '').toLowerCase();
    if (!orgKey) return;

    const cacheKey = `fieldforce_branding_${orgKey}`;
    const TTL_MS = 12 * 60 * 60 * 1000;
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached?.expires_at && cached.expires_at > Date.now()) {
          setBranding({ title: cached.title || null, logo_url: cached.logo_url || null });
          return;
        }
        localStorage.removeItem(cacheKey);
      }
    } catch (_) { /* corrupt cache — ignore and refetch */ }

    axios.get('/api/config/org-branding', { params: { organization: orgKey } })
      .then(res => {
        if (!res?.data) return;
        const entry = {
          title: res.data.title || null,
          logo_url: res.data.logo_url || null,
          expires_at: Date.now() + TTL_MS,
        };
        setBranding({ title: entry.title, logo_url: entry.logo_url });
        try { localStorage.setItem(cacheKey, JSON.stringify(entry)); } catch (_) {}
      })
      .catch(() => {});
  }, [isAuthenticated, user?.organization, storedAuth.organization]);

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setLoading(true);
    setIsStaging(userData?.requested_db === 'staging');
    setIsStagingMachine(!!(userData?.is_staging));
    if (userData?.organization) {
      axios.defaults.headers.common['X-Organization'] = userData.organization;
    } else {
      delete axios.defaults.headers.common['X-Organization'];
    }
    if (userData?.requested_db) {
      axios.defaults.headers.common['X-Db-Choice'] = userData.requested_db;
    } else {
      delete axios.defaults.headers.common['X-Db-Choice'];
    }
  };

  const handleLogout = async () => {
    // Clear server session first (e.g. db_choice on staging) so next login gets a fresh choice.
    // Await logout so that when Login mounts, server session is already cleared.
    try {
      await axios.post('/api/auth/logout');
    } catch (_) {}
    localStorage.removeItem('fieldforce_auth');
    setIsAuthenticated(false);
    setUser(null);
    setIsStaging(false);
    setCurrentModule('sales-home');
    setOrgTabs(null);
    setModuleRegistry([]);
    setBranding({ title: null, logo_url: null });
    setFilters({
      time_range: '365',
      date_from: null,
      date_to: null,
      province: null,
      region_id: null,
      team_id: null,
      channel_id: null,
      client_type_id: null,
      agent_id: null,
      date_by: 'call_date',
    });
    setDrilldown(null);
    delete axios.defaults.headers.common['X-Organization'];
    delete axios.defaults.headers.common['X-Db-Choice'];
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleDrilldown = (type, id, context) => {
    setDrilldown({ type, id, context });
  };

  const closeDrilldown = () => {
    setDrilldown(null);
  };

  const handleNavigateToConversation = (convId) => {
    setDrilldown(null);
    setFocusedConversationId(convId);
    setCurrentModule('field-hq');
  };

  const renderModule = () => {
    const moduleProps = {
      filters,
      onDrilldown: handleDrilldown,
      loading,
      setLoading,
      isStaging,
    };

    switch (currentModule) {
      // Sales tabs (existing React dashboard)
      case 'sales-home':
        return <Home {...moduleProps} />;
      case 'sales-signal':
        return <FieldSignal {...moduleProps} />;
      case 'sales-intel':
        return <FieldIntel {...moduleProps} />;
      case 'sales-sentiment':
        return <SentimentIntel {...moduleProps} />;
      case 'sales-ops':
        return <FieldOps {...moduleProps} />;
      case 'sales-strategy':
        return <FieldStrategy {...moduleProps} />;
      // Agri tabs (from Flask dashboard)
      case 'agri-home':
        return <AgriHome {...moduleProps} />;
      case 'agri-marketing':
        return <AgriMarketing {...moduleProps} />;
      case 'agri-operations':
        return <AgriOperations {...moduleProps} />;
      case 'agri-engagement':
        return <AgriEngagement {...moduleProps} />;
      case 'agri-audio':
        return <AgriAudio {...moduleProps} currentUser={user} />;
      // Agent View (per-agent drill-down) and Agent Compare (two-agent comparison)
      case 'agent-view':
        return <AgentView {...moduleProps} />;
      case 'agent-compare':
        return <AgentCompare {...moduleProps} />;
      // New dashboard pages (staging_only in the module registry)
      case 'market-brief':
        return <MarketBrief {...moduleProps} />;
      case 'brand-intel-v2':
        return <BrandIntelV2 {...moduleProps} />;
      case 'field-intel-v2':
        return <FieldIntelV2 {...moduleProps} />;
      case 'analytics':
        return <Analytics {...moduleProps} />;
      case 'field-team-v2':
        return <FieldTeamV2 {...moduleProps} currentUser={user} />;
      case 'help':
        return <Help />;
      // HR tabs
      case 'hr-retention':
        return <HrRetention {...moduleProps} />;
      case 'hr-explorer':
        return <HrExplorer {...moduleProps} />;
      // Shared tabs
      case 'field-hq':
        return <FieldHQ {...moduleProps} currentUser={user} focusedConversationId={focusedConversationId} onFocusHandled={() => setFocusedConversationId(null)} onOrgSettingsChange={(s) => { if (s.default_time_range) setFilters(f => ({ ...f, time_range: s.default_time_range })); }} />;
      case 'dachido-admin':
        if (user?.organization !== 'Dachido' || user?.username !== 'admin_dachido') {
          return <FieldHQ {...moduleProps} currentUser={user} focusedConversationId={focusedConversationId} onFocusHandled={() => setFocusedConversationId(null)} onOrgSettingsChange={(s) => { if (s.default_time_range) setFilters(f => ({ ...f, time_range: s.default_time_range })); }} />;
        }
        return <DachidoAdmin currentUser={user} />;
      case 'admin-tasks':
        return <AdminTasks currentUser={user} />;
      case 'org-config':
        return <OrgConfig currentUser={user} />;
      case 'system':
        return <System currentUser={user} />;
      case 'custom-widgets':
        return <CustomWidgets {...moduleProps} currentUser={user} />;
      default:
        return <Home {...moduleProps} />;
    }
  };

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <FilterContext.Provider value={{ filters, setFilters: handleFilterChange }}>
      <ReportProvider filters={filters}>
      <div className="app">
        {/* HEADER */}
        <header className="app-header">
          <div className="header-left">
            {branding.logo_url ? (
              <img
                className="app-logo-img"
                src={branding.logo_url}
                alt={branding.title || 'Logo'}
                onError={e => {
                  e.currentTarget.style.display = 'none';
                  setBranding(b => ({ ...b, logo_url: null }));
                  const orgKey = (user?.organization || storedAuth.organization || '').toLowerCase();
                  if (orgKey) {
                    try { localStorage.removeItem(`fieldforce_branding_${orgKey}`); } catch (_) {}
                  }
                }}
              />
            ) : (
              <div className="app-logo">FI</div>
            )}
            <div className="app-title">{branding.title || 'Field Intelligence'}</div>
          </div>

          {/* CENTER NAVIGATION */}
          <nav className="header-nav">
            {modules
              .filter(module => {
                // admin_only modules are hidden from non-admin roles
                if (module.admin_only && user?.role !== 'admin') {
                  return false;
                }
                return true;
              })
              .map(module => (
                <button
                  key={module.id}
                  type="button"
                  className={`nav-link ${currentModule === module.id ? 'active' : ''} ${module.disabled ? 'disabled' : ''}`}
                  onClick={() => { if (!module.disabled) { setLoading(true); setCurrentModule(module.id); } }}
                  disabled={module.disabled}
                  aria-current={currentModule === module.id ? 'page' : undefined}
                >
                  {module.label}
                </button>
              ))}
          </nav>

          {/* RIGHT - User and logout (no env badge) */}
          <div className="header-right">
            {moduleMap[currentModule]?.report_builder && <ReportsMenu />}
            <div className="user-menu" title={`${user?.username || 'User'} (${user?.organization || ''})`}>
              <div className="user-avatar">{user?.organization?.substring(0, 2) || 'DR'}</div>
              <span>{user?.username || 'User'}</span>
            </div>
            <button
              type="button"
              className="header-logout-btn"
              onClick={handleLogout}
              title="Log out"
            >
              Logout
            </button>
          </div>
        </header>

        <div className="main-container">
          {/* Main Content */}
          <main className="main-content">
            {renderModule()}
            {currentModule !== 'custom-widgets' && (
              <PageWidgets pageId={currentModule} filters={filters} />
            )}
          </main>

          {/* Drilldown Panel */}
          {drilldown && (
            <DrilldownPanel
              drilldown={drilldown}
              filters={filters}
              onClose={closeDrilldown}
              onNavigateToConversation={handleNavigateToConversation}
            />
          )}
        </div>
      </div>
      </ReportProvider>
    </FilterContext.Provider>
  );
}

export default App;
